'use client';

import { useEffect } from 'react';
import { useUser } from '@auth0/nextjs-auth0';
import { usePathname, useRouter } from 'next/navigation';
import styles from './LeaveConversationModal.module.scss';
import XCloseIcon from './icons/XCloseIcon';
import CaretIcon from './icons/CaretIcon';
import {
  captureAccountSignupStarted,
  capturePlanBrowseStarted,
} from 'lib/analytics';

interface LeaveConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function LeaveConversationModal({
  isOpen,
  onClose,
}: LeaveConversationModalProps) {
  const { user } = useUser();
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleBrowsePlans = () => {
    capturePlanBrowseStarted('leave_conversation_modal');
    router.push('/browse/plans');
  };

  const handleCreateAccount = () => {
    captureAccountSignupStarted('leave_conversation_modal');
    router.push(`/auth/login?returnTo=${pathname}&screen_hint=signup`);
  };

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={styles.modalWrapper}>
        <div className={styles.modal}>
          <div className={styles.accentLine} />
          <div className={styles.content}>
            {user ? (
              <>
                <div className={styles.textContent}>
                  <p className={styles.title}>
                    You&rsquo;re leaving the conversation...{' '}
                    <span>
                      But don&rsquo;t worry, we&rsquo;ve saved your progress!
                    </span>
                  </p>
                  <p className={styles.body}>
                    You can come back anytime to get a recommendation.
                    We&rsquo;ll pick up right where you left off.
                  </p>
                </div>
                <div className={styles.actions}>
                  <button
                    className={`button primary ${styles.primaryButton}`}
                    onClick={onClose}
                  >
                    Actually, I&rsquo;d like a recommendation now
                  </button>
                  <button
                    className={styles.browseButton}
                    onClick={handleBrowsePlans}
                  >
                    Browse plans in my area
                    <CaretIcon direction="right" color="purple" size="large" />
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className={styles.textContent}>
                  <p className={styles.title}>
                    Do you want to create an account and save your progress?
                  </p>
                  <p className={styles.body}>
                    If you decide to get a recommendation later, you can skip
                    answering the same questions again.
                  </p>
                </div>
                <div className={styles.actions}>
                  <button
                    className={`button primary ${styles.primaryButton}`}
                    onClick={handleCreateAccount}
                  >
                    Create an account
                  </button>
                  <button
                    className={styles.browseButton}
                    onClick={handleBrowsePlans}
                  >
                    Browse plans in my area
                    <CaretIcon direction="right" color="purple" size="large" />
                  </button>
                </div>
              </>
            )}
          </div>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            <XCloseIcon />
          </button>
        </div>
      </div>
    </>
  );
}
